import { Pencil } from 'lucide-react'
import { Badge, Button } from '@bb/ui'
import { de } from '../../i18n'
import { settingValidityLabel, settingValueDisplay } from './settingsForm'
import type { Setting } from './types'

const GRID_TEMPLATE = 'minmax(11rem,1.1fr) minmax(14rem,2fr) minmax(10rem,1.6fr) 7rem 2.5rem'

export interface SettingsTableProps {
  settings: Setting[]
  onEdit: (setting: Setting) => void
}

/** §8's table, in UsersTable's own idiom (task 7) — a CSS grid, one row per
    setting, no sort or search: `listSettings` already returns the small,
    fixed set from the seed migration (§8.3) in name order. */
export function SettingsTable({ settings, onEdit }: SettingsTableProps) {
  return (
    <div className="min-w-0 overflow-x-auto rounded-[var(--radius-panel)] border border-rule">
      <div style={{ minWidth: '46rem' }}>
        <div
          role="row"
          className="grid items-center gap-3 border-b border-rule-strong bg-surface px-4 py-2 text-xs font-medium text-ink-muted"
          style={{ gridTemplateColumns: GRID_TEMPLATE }}
        >
          <span>{de.settings.columns.name}</span>
          <span>{de.settings.columns.description}</span>
          <span>{de.settings.columns.value}</span>
          <span>{de.settings.columns.forFrontend}</span>
          <span aria-hidden />
        </div>

        <div className="divide-y divide-rule">
          {settings.map((setting, index) => (
            <div
              key={setting.name}
              role="row"
              tabIndex={0}
              onClick={() => onEdit(setting)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onEdit(setting)
                }
              }}
              className={`grid cursor-default items-center gap-3 px-4 py-2 text-sm hover:bg-sunken ${index % 2 === 1 ? 'bg-band' : ''}`}
              style={{ gridTemplateColumns: GRID_TEMPLATE }}
            >
              <span className="min-w-0 truncate font-mono text-xs text-ink" title={setting.name}>{setting.name}</span>
              <span className="min-w-0 truncate text-ink-muted" title={setting.description || undefined}>
                {setting.description || <span className="text-ink-faint">—</span>}
              </span>
              <span className="min-w-0 truncate text-ink">
                {setting.isSecret ? (
                  <Badge tone="neutral">{settingValueDisplay(setting)}</Badge>
                ) : (
                  settingValueDisplay(setting)
                )}
                {settingValidityLabel(setting) ? (
                  <span className="block truncate text-xs text-ink-faint">{settingValidityLabel(setting)}</span>
                ) : null}
              </span>
              <span>
                {setting.forFrontend ? <Badge tone="accent">{de.settings.yes}</Badge> : <span className="text-ink-faint">{de.settings.no}</span>}
              </span>

              <span onClick={(event) => event.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${de.settings.rowMenu.edit} „${setting.name}“`}
                  onClick={() => onEdit(setting)}
                >
                  <Pencil className="size-4" />
                </Button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
